import React, { useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";

import { USER_TYPES } from "lookup";

import ImageViewer from "components/ImageViewer";
import CloudinaryWidget from "components/CloudinaryWidget";
import { MatchContext, SearchContext } from "context/providers";
import { cn } from "utils";
import MatchScoreBadge from "components/UserCardList/components/MatchScoreBadge";

import placeholderPicture from "images/profile-dummy-photo.png";

const DummyPhoto = () => (
  <img
    src={placeholderPicture}
    className="w-full h-full rounded-lg"
    alt="profile"
  />
);

const UserContacts = ({ hit, rankDetails }) => {
  const { updateMatchUserFieldLocally, matches } = useContext(MatchContext);
  const { updateHit } = useContext(SearchContext);

  const [updatingProfilePicture, setUpdatingProfilePicture] = useState(false);

  const saveAttributes = async (attributes) => {
    if (attributes.headshotKey) {
      setUpdatingProfilePicture(true);
    }

    const attributesToUpdate = {
      input: {
        id: hit.id,
        ...attributes,
      },
    };

    try {
      await updateHit(attributesToUpdate);

      if (matches.length > 0) {
        const applicationId =
          hit.match?.applicationId ||
          matches.find(({ userId }) => userId === hit.id)?.applicationId;

        if (!applicationId) {
          return;
        }

        updateMatchUserFieldLocally(
          applicationId,
          "headshotKey",
          attributes.headshotKey
        );
      }
    } catch (error) {
      console.log("Error when updating hit", error);
    }
  };

  useEffect(() => {
    if (updatingProfilePicture === true) {
      setTimeout(() => {
        setUpdatingProfilePicture(false);
      }, 1500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hit]);

  const renderCustomerTag = () => {
    if (hit.userType !== USER_TYPES.CUSTOMER) {
      return null;
    }
    return (
      <div className="w-[90%] mt-4 flex flex-col gap-y-1">
        <p className="text-center bg-[#0495b7] text-white rounded">Customer</p>
        <p className="text-center truncate">{hit.company}</p>
      </div>
    );
  };

  return (
    <div className="flex flex-col flex-none w-[125px] items-start">
      <div className="flex flex-col gap-y-2 p-2">
        <div className="relative w-[100px] h-[100px]">
          <div className="w-full h-full overflow-hidden rounded-lg">
            {hit.headshotKey ? (
              <ImageViewer
                objectKey={hit.headshotKey}
                width={100}
                responsive={false}
              />
            ) : (
              <DummyPhoto />
            )}
          </div>
          {updatingProfilePicture ? (
            <div className="flex items-center justify-center h-full w-full absolute top-0 left-0 opacity-60 bg-white pl-2">
              <p className="text-gray-800 text-sm">Updating photo...</p>
            </div>
          ) : (
            <div
              className={cn(
                "flex justify-center items-center h-full w-full absolute top-0 left-0 transition-all duration-500 bg-white",
                {
                  "opacity-0 hover:opacity-90":
                    hit.headshotKey || !hit.headshotKey,
                }
              )}
            >
              <CloudinaryWidget
                onFinishAction={(cloudinaryPublicId, remove) => {
                  if (!cloudinaryPublicId && !remove) {
                    return;
                  }
                  saveAttributes({ headshotKey: cloudinaryPublicId });
                }}
                identifier="profile-pic-upload"
                minImageWidth={317}
                minImageHeight={317}
                imageDimension="317 pixels wide by 317 pixels tall"
                resourceType="image"
                showWarningModal={hit.headshotKey}
                warningModalMessage={
                  <>
                    <br />
                    <span>
                      Are you sure you want to upload profile picture for this
                      user?
                    </span>
                    <br />
                    <span>
                      Uploading a photo will replace current profile picture.
                    </span>
                  </>
                }
                allowDelete={hit.headshotKey}
                deleteModalMessage={
                  <>
                    <br />
                    <span>
                      Are you sure you want to remove the profile picture for
                      this user?
                    </span>
                  </>
                }
              />
            </div>
          )}
          {rankDetails?.score !== undefined && (
            <MatchScoreBadge
              ideal={false}
              score={rankDetails.score}
              label="Match: "
              variant="avatarOverlay"
              className="absolute bottom-0 left-0 z-20 h-6 px-2 text-xs"
            />
          )}
        </div>
      </div>

      {renderCustomerTag()}
    </div>
  );
};

UserContacts.propTypes = {
  hit: PropTypes.object,
  rankDetails: PropTypes.object,
};

UserContacts.defaultProps = {
  hit: {},
  rankDetails: null,
};

export default UserContacts;
